"use client";

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { t } from "@/lib/site-texts";
import ProductCard from "@/components/ProductCard";
import CategoryTileSlider from "@/components/CategoryTileSlider";
import Pagination from "@/components/Pagination";
import ShopSidebar, { ActiveFilterChips, applyFilters, DEFAULT_FILTERS, formatProductCount, ShopFilters } from "@/components/ShopSidebar";
import FeaturedProductsSlider from "@/components/FeaturedProductsSlider";
import CategoryContentAccordion from "@/components/CategoryContentAccordion";
import CategoryFaqSection from "@/components/CategoryFaqSection";
import type { Product, ColorOption, RichCategory } from "@/types";
import { useCart } from "@/context/CartContext";
import { usePagePagination } from "@/lib/use-page-pagination";
import { filtersFromQuery } from "@/lib/filter-query"; // MOW-FILTERLINKS-OKT26
import { useFilterUrlWriter } from "@/lib/use-filter-url-writer"; // MOW-FILTERLINKS-OKT26
import { useLaadNaInteractie } from "@/lib/use-load-after-intent"; // MOW-PERF-OKT26
// >>> MOW-CATEGORIEFILTER-OKT26
import CategoryBlocks from "@/components/CategoryBlocks";
import { productIsOnSale } from "@/lib/product-sale";
import { HOOFD_CATEGORIEEN } from "@/lib/category-filter";
import { useCategoryTree } from "@/lib/navigation-client";
// <<< MOW-CATEGORIEFILTER-OKT26

// Aantal kaarten per pagina (mobile-first paginering i.p.v. infinite scroll, zie G10)
const PAGE_SIZE = 30;

type CardEntry = { key: string; product: Product; colorOption?: ColorOption };
import { laadFirestore } from "@/lib/firestore-client"; // MOW-PERF-OKT26: Firestore pas laden als het nodig is
import { SHOP_TILES } from "@/lib/shop-tiles"; // MOW-PERF-OKT26
import { isProductZichtbaar } from "@/lib/product-visibility";
import { compareNewest, shouldSpreadCards, spreadCardsPerProduct } from "@/lib/product-sort";

function ShopPageInner({ initialProducts, initialTotalCards, initialCategoryBanners, initialSaleCategorie }: { initialProducts: Product[]; initialTotalCards?: number; initialCategoryBanners?: Record<string, string | undefined>; initialSaleCategorie?: RichCategory }) {
  const searchParams = useSearchParams();
  // "Nieuwste producten" (?nieuw=1) opent op "Nieuwste eerst"; elke andere weergave op "Bestverkocht".
  // Die standaard bepaalt wat er in het adres komt (lib/filter-query.ts) en waar "Filters wissen" naartoe gaat.
  const nieuwNu = searchParams.get("nieuw") === "1";
  const paginaStandaardSort = nieuwNu ? "newest" : DEFAULT_FILTERS.sort;
  // Server-gerenderd meegegeven: alleen de eerste pagina (zie app/shop/
  // page.tsx — de hele catalogus server-side meegeven bleek te zwaar voor
  // de resource-beperkte hosting). Zo staat er bij het eerste bezoek meteen
  // écht content i.p.v. "Producten laden…" (G14, trage advertentie-
  // landingen op mobiel). Het volledige assortiment (nodig om te filteren/
  // sorteren/pagineren voorbij pagina 1) halen we hierna alsnog op de
  // achtergrond op, zoals voorheen — alleen niet meer blokkerend voor de
  // eerste weergave.
  const [products, setProducts] = useState<Product[]>(initialProducts);
  // >>> MOW-PERF-OKT26
  // De volledige catalogus (±900 KB over de lijn, ±5 MB uitgepakt) wordt pas
  // opgehaald als de bezoeker iets doet (scrollen, tikken, filteren) of een tijd
  // na het laden, niet meer midden in het laden: dat liep voor de hydratatie en
  // voor de instellingen (pop-up, teksten) uit en blokkeerde de hoofdthread.
  // De eerste pagina uit de server is precies de pagina die hierna ook uit de
  // volledige lijst komt (lib/product-list-data.ts), dus er wisselt niets.
  const [volledig, setVolledig] = useState(false);
  const [laadMislukt, setLaadMislukt] = useState(false); // MOW-CATEGORIEFILTER-OKT26: bij een fout niet eeuwig "laden" tonen
  useLaadNaInteractie(
    () => {
      (async () => {
        try {
          // Geen where("status",...)/orderBy("createdAt"): zie toelichting in
          // app/shop/page.tsx — producten zonder dat veld zouden anders stil
          // uit een gefilterde/gesorteerde Firestore-query vallen.
          const { db, collection, getDocs } = await laadFirestore(); // MOW-PERF-OKT26
          const snap = await getDocs(collection(db, "products"));
          const items = snap.docs.map((d) => ({ ...d.data(), id: d.id })) as Product[];
          // Zelfde "nieuwste"-definitie als de sortering in applyFilters (lib/product-sort.ts).
          items.sort((a, b) => compareNewest(a, b));
          setProducts(items.filter(isProductZichtbaar));
          setVolledig(true);
        } catch (err) {
          console.error("Fout bij (achtergrond) laden volledige shop-catalogus:", err);
          setLaadMislukt(true); // MOW-CATEGORIEFILTER-OKT26
        }
      })();
    },
    // Uitstellen kan alleen als de server de standaardweergave meegaf (initialTotalCards). Met een
    // zoekterm of filter in het adres (de 30 nieuwste producten als eerste pagina) of zonder
    // eerste pagina is de volledige lijst meteen nodig, zoals voorheen.
    { direct: initialProducts.length === 0 || initialTotalCards === undefined }
  );
  // Zolang alleen de eerste pagina er is, geeft de server het echte aantal door:
  // "30 producten" zou op een catalogus van meer dan 250 kaarten een verkeerd getal tonen.
  // Verandert de bezoeker een filter, dan telt de lijst zelf weer.
  const [telZelf, setTelZelf] = useState(false);
  // <<< MOW-PERF-OKT26
  const [filters, setFilters] = useState<ShopFilters>(DEFAULT_FILTERS);
  const [saleOnly, setSaleOnly] = useState(false);
  const [nieuwOnly, setNieuwOnly] = useState(false);
  const { promotions } = useCart();
  // MOW-CATEGORIEFILTER-OKT26: op /shop is Sale de eigen weergave (?sale=1: kop, SEO-blokken); het vakje in het paneel en de chip
  // zetten die weergave aan of uit. Daarom komt de Sale-stand voor het paneel uit de weergave en niet uit `filters`.
  const filtersMetSale = useMemo<ShopFilters>(() => ({ ...filters, sale: saleOnly }), [filters, saleOnly]);

  // Live omslagfoto's per categorie-slug (zie lib/shop-tiles.ts). MOW-PERF-OKT26: de server zoekt
  // ze op en geeft ze mee (app/shop/page.tsx). Voorheen deed de client hier bij het laden een
  // Firestore-query voor, waarvoor de hele SDK (±470 KB geparseerd) binnen moest komen, en
  // wisselden de tegelfoto's na hydratatie van de vaste fallback naar de ingestelde omslagfoto.
  // Ontbreekt een foto (leeg object bij een serverfout), dan blijft de vaste fallback staan.
  const categoryBanners = initialCategoryBanners ?? {};
  // SEO-content (intro, blokken, FAQ, uitgelicht) die de eigenaar bij de
  // categorie "Sale" in de admin invult — deze pagina las die nooit, dus alles
  // wat daar werd ingevuld bleef onzichtbaar.
  // MOW-PERF-OKT26: op /shop?sale=1 levert de server de categorie al mee (app/shop/page.tsx), dus
  // zonder Firestore-SDK en zonder wisseling van de vaste kop. Alleen zonder (serverfout, of een
  // client-navigatie naar ?sale=1 waarbij de server niets meegaf) haalt de client hem zelf op.
  const [saleCategorieClient, setSaleCategorie] = useState<RichCategory | null>(null);
  const saleCategorie = initialSaleCategorie ?? saleCategorieClient;
  useEffect(() => {
    if (!saleOnly || initialSaleCategorie) return;
    let cancelled = false;
    (async () => {
      const { db, doc, getDoc } = await laadFirestore(); // MOW-PERF-OKT26
      const snap = await getDoc(doc(db, "categories", "sale"));
      if (!cancelled && snap.exists()) setSaleCategorie({ id: "sale", ...snap.data() } as RichCategory);
    })();
    return () => { cancelled = true; };
  }, [saleOnly, initialSaleCategorie]);
  const saleFeaturedProducts = useMemo(() => {
    const ids = saleCategorie?.featuredProductIds || [];
    if (ids.length === 0) return [];
    const byId = new Map(products.map((p) => [p.id, p]));
    return ids.map((id) => byId.get(id)).filter((p): p is Product => !!p);
  }, [saleCategorie, products]);

  // Een product staat "in de sale" als het is afgeprijsd óf onder een lopende actie valt.
  // MOW-CATEGORIEFILTER-OKT26: de regel staat in lib/product-sale.ts, gedeeld met de categorie- en merkpagina.
  const isOnSale = React.useCallback((p: Product) => productIsOnSale(p, promotions), [promotions]);
  const { tree: categoryTree } = useCategoryTree();

  // Zoekterm reactief op de URL (?zoek=): bij een client-navigatie vanaf het
  // header-loepje verandert alleen de query, niet de route — een mount-only
  // effect zou dan niet opnieuw draaien en de resultaten niet verversen.
  // >>> MOW-FILTERS-OKT26 6: de zoekterm uit het adres alleen overnemen als ?zoek=
  // zelf veranderd is (header-loepje, Terug, gedeelde link), niet bij elke
  // adreswijziging. Een paginaklik wijzigt het adres ook (?pagina=2) en wiste
  // daardoor de term die in de zijbalk was getypt (B18). `null` = nog niet
  // gelezen: bij het eerste renderen nemen we ?zoek= altijd over.
  const laatsteZoekRef = useRef<string | null>(null);
  // >>> MOW-FILTERLINKS-OKT26: de gekozen filters staan ook in het adres. Schrijft de hook ?zoek=, dan
  // onthoudt laatsteZoekRef dat meteen, zodat het effect hieronder dat niet als een wijziging van
  // buitenaf ziet (en de zoekterm die de bezoeker net typte niet terugzet).
  const filterUrl = useFilterUrlWriter({
    defaultSort: paginaStandaardSort,
    onWritten: (nieuweQuery) => {
      laatsteZoekRef.current = new URLSearchParams(nieuweQuery).get("zoek") || "";
    },
  });
  // <<< MOW-FILTERLINKS-OKT26
  useEffect(() => {
    // MOW-FILTERLINKS-OKT26: het adres uit window.location, niet uit de snapshot van useSearchParams:
    // Next verwerkt history.replaceState via een transitie, dus die snapshot loopt even achter op
    // wat de hook net schreef en zou een toets terugdraaien.
    const z = new URLSearchParams(window.location.search).get("zoek") || "";
    if (laatsteZoekRef.current !== z) {
      laatsteZoekRef.current = z;
      filterUrl.cancelPending(); // MOW-FILTERLINKS-OKT26: het adres is door iets anders gewijzigd
      setFilters((f) => (f.search === z ? f : { ...f, search: z }));
    }
    // <<< MOW-FILTERS-OKT26 6
    setSaleOnly(searchParams.get("sale") === "1");
    setNieuwOnly(searchParams.get("nieuw") === "1");
  }, [searchParams, filterUrl]);

  // "Nieuwste producten" (?nieuw=1, homepage en menu) opent op "Nieuwste eerst" in
  // plaats van op "Bestverkocht" (feedback 14-10-2026). Alleen op de OVERGANG naar
  // ?nieuw=1 en alleen als het adres zelf geen ?sort= heeft: het adres verandert
  // ook bij ?pagina=2, en een door de bezoeker gekozen sortering mag daar niet door
  // terugspringen. Gaat de bezoeker via een client-navigatie terug naar de gewone
  // winkel, dan zetten we de sortering alleen terug als wíj hem zetten.
  // Tijdens het renderen bijgewerkt (React-patroon "state aanpassen op een
  // wijziging van een prop"), niet in een effect: dat gaf een eerste render met de
  // verkeerde sortering en een tweede erachteraan.
  const [vorigeNieuw, setVorigeNieuw] = useState<boolean | null>(null);
  const [sortDoorNieuwGezet, setSortDoorNieuwGezet] = useState(false);
  if (vorigeNieuw !== nieuwNu) {
    setVorigeNieuw(nieuwNu);
    if (nieuwNu && vorigeNieuw !== true) {
      // Alleen als de sortering nu nog de standaard is: een eerder gekozen sortering blijft zoals ze is en
      // wordt bij het verlaten van deze weergave dus ook niet teruggezet.
      if (!searchParams.get("sort") && filters.sort === DEFAULT_FILTERS.sort) {
        setSortDoorNieuwGezet(true);
        setFilters((f) => ({ ...f, sort: "newest" }));
      }
    } else if (!nieuwNu && vorigeNieuw === true && sortDoorNieuwGezet) {
      setSortDoorNieuwGezet(false);
      setFilters((f) => (f.sort === "newest" ? { ...f, sort: DEFAULT_FILTERS.sort } : f));
    }
  }

  // >>> MOW-FILTERLINKS-OKT26: de overige filters uit het adres (merk, kleur, maat, materiaal, prijs,
  // sortering). Eerst met de eerste pagina uit de server, zodat een gedeelde link meteen gefilterd
  // toont, en opnieuw zodra de volledige catalogus er is: de schrijfwijze van merken, kleuren en maten
  // komt van de producten. Het adres is de waarheid (elke wijziging van de bezoeker wordt erin
  // geschreven), dus opnieuw toepassen verandert niets aan wat hij al koos. ?zoek= doet het effect hierboven.
  useEffect(() => {
    const uitAdres = filtersFromQuery(window.location.search, products, {
      skip: ["zoek", "sale"], // MOW-CATEGORIEFILTER-OKT26: Sale is hier de weergave (saleOnly uit het adres), geen filterstand
      defaultSort: new URLSearchParams(window.location.search).get("nieuw") === "1" ? "newest" : undefined,
    });
    if (Object.keys(uitAdres).length > 0) setFilters((f) => ({ ...f, ...uitAdres }));
  }, [products]);
  // <<< MOW-FILTERLINKS-OKT26

  const matchesTile = (p: Product, slug: string) =>
    p.subcategorySlug === slug || p.categorySlug === slug || (p.categorySlugs || []).includes(slug);

  // Telt kleurvarianten, niet producten (zelfde eenheid als "N producten"
  // verderop op deze pagina en op de categoriepagina's, en als
  // countProductsPerCategorySlug op de homepage) — anders toonden de
  // tegels hier een ander (lager) aantal dan overal elders op de site.
  const countFor = (slug: string) =>
    products
      .filter((p) => matchesTile(p, slug))
      .reduce((sum, p) => sum + Math.max(1, (p.colorOptions || []).length), 0);

  // Product is "nieuw" als het product zelf zo gemarkeerd is, óf minstens één
  // kleurvariant (zelfde regel als de badge op ProductCard) — anders viel een
  // product met maar één nieuwe kleur helemaal weg op de ?nieuw=1-weergave.
  const isNewEntry = (p: Product, co?: ColorOption) =>
    !!p.new || (co ? !!co.isNew : (p.colorOptions || []).some((o) => o.isNew));

  // >>> MOW-FILTERS-OKT26 2: de zijbalk krijgt alleen de producten binnen de scope
  // (Nieuw / Sale) en niet de hele catalogus. Zo kloppen de MERKEN: op Nieuw
  // 10 merken i.p.v. 37, en "Beeren ondergoed" (0 nieuwe producten) staat er niet
  // meer tussen. Dit lost de maat- en kleurlijst NIET op: de Nieuw-weergave
  // werkt op kaartniveau (alleen de nieuwe kleurvarianten), dit scopet op
  // productniveau; kaartniveau is Fase 1. Zonder scope: dezelfde array (geen kopie).
  // Alleen voor de zijbalk: de lijst zelf (`visible`) blijft ongewijzigd.
  const scopedProducts = useMemo(
    () =>
      saleOnly || nieuwOnly
        ? products
            .filter((p) => (!saleOnly || isOnSale(p)) && (!nieuwOnly || isNewEntry(p)))
            // MOW-CATEGORIEFILTER-OKT26: op Nieuw toont het raster alleen de nieuwe kleuren (zie `cards`); de aantallen in de
            // zijbalk (categorieën) tellen dan ook alleen die kaarten, anders staat er "Heren (32)" boven een raster van 13.
            .map((p) => (nieuwOnly && !p.new && p.colorOptions?.length ? { ...p, colorOptions: p.colorOptions.filter((o) => o.isNew) } : p))
        : products,
    [products, saleOnly, nieuwOnly, isOnSale]
  );
  // <<< MOW-FILTERS-OKT26 2

  const filtered = useMemo(() => applyFilters(products, filters), [products, filters]);
  const visible = useMemo(() => {
    let list = filtered;
    if (saleOnly) list = list.filter(isOnSale);
    if (nieuwOnly) list = list.filter((p) => isNewEntry(p));
    return list;
  }, [filtered, saleOnly, nieuwOnly, isOnSale]);

  // Eén kaart per kleurvariant — als platte lijst zodat we progressief kunnen renderen.
  // Op de ?nieuw=1-weergave alleen de kaarten die zelf nieuw zijn (i.p.v. alle
  // kleuren van een product waarvan er maar één als nieuw is gemarkeerd).
  // Bij "Bestverkocht" en "Nieuwste eerst" staan eerst de eerste kleuren van alle
  // producten, dan de tweede kleuren enz. (lib/product-sort.ts): een product met 33
  // kleuren vulde anders een hele pagina (feedback 14-10-2026).
  const cards = useMemo<CardEntry[]>(() => {
    const kaarten = visible.flatMap((p): CardEntry[] => {
      let options = p.colorOptions && p.colorOptions.length > 0 ? p.colorOptions : undefined;
      if (nieuwOnly && !p.new && options) options = options.filter((o) => o.isNew);
      return options
        ? options.map((opt) => ({ key: `${p.id}-${opt.slug}`, product: p, colorOption: opt }))
        : [{ key: p.id, product: p }];
    });
    return shouldSpreadCards(filters.sort) ? spreadCardsPerProduct(kaarten) : kaarten;
  }, [visible, nieuwOnly, filters.sort]);

  // MOW-PERF-OKT26: het getal voor teller, paneelknop en zijbalk (zie telZelf hierboven)
  const aantalKaarten = volledig || telZelf || initialTotalCards === undefined ? cards.length : initialTotalCards;
  // MOW-CATEGORIEFILTER-OKT26: een categorie kiezen vóór de volledige catalogus binnen is (er is dan alleen de eerste
  // pagina) zou "Geen producten gevonden" of een onvolledig raster tonen; liever even "laden" tot de lijst er is.
  const wachtOpLijst = !volledig && !laadMislukt && filters.categories.length > 0;

  // Paginering (G10): pagina zit in de URL (?pagina=N), niet in lokale state.
  const { page, goToPage, resetPage, gridRef } = usePagePagination();
  const totalPages = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageCards = cards.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // >>> MOW-FILTERS-OKT26 1: terug naar pagina 1 hoort bij een wijziging door de
  // bezoeker (filter, sortering, chip, zoekterm), niet bij elke render waarin
  // `filters` van identiteit wisselt: het eerdere effect draaide ook bij het
  // eerste renderen en wiste daardoor ?pagina=2 uit een gedeelde link en bij
  // Terug vanaf een product (B1). De Sale-/Nieuw-weergave komt uit het adres
  // zelf (menulink zonder ?pagina=), die hoeft hier niet te resetten.
  // MOW-FILTERS-OKT26 6: is de zoekterm gewist, dan gaat ?zoek= in dezelfde
  // adreswijziging weg (anders komt de term bij herladen terug, B12). Het
  // geheugen van het effect hierboven wordt meteen bijgewerkt, zodat een
  // toets direct na het wissen niet door de latere URL-wijziging wordt overschreven.
  const changeFilters = useCallback(
    (next: ShopFilters) => {
      setFilters(next);
      setSaleOnly(next.sale); // MOW-CATEGORIEFILTER-OKT26: de weergave volgt het Sale-vakje meteen; het adres volgt via filterUrl.write
      setTelZelf(true); // MOW-PERF-OKT26
      // Een andere sortering gekozen door de bezoeker is niet meer onze standaard.
      if (next.sort !== "newest") setSortDoorNieuwGezet(false);
      if (next.search === "") {
        laatsteZoekRef.current = "";
        resetPage({ dropParams: ["zoek"] });
      } else {
        resetPage();
      }
      filterUrl.write(next); // MOW-FILTERLINKS-OKT26
    },
    [resetPage, filterUrl]
  );
  // <<< MOW-FILTERS-OKT26 1

  return (
    <div className="container" style={{ paddingTop: "30px", paddingBottom: "60px" }}>
      {/* Breadcrumbs */}
      <div style={{ fontSize: "0.8rem", color: "var(--color-gray-400)", marginBottom: "20px" }}>
        <Link href="/" style={{ color: "inherit", textDecoration: "none" }}>HOME</Link>
        {" / "}
        <span style={{ color: "var(--color-secondary)", fontWeight: 500 }}>WINKEL</span>
      </div>

      {/* Categorie-tegels — zelfde slider als de homepage ("Alles voor onder
          je outfit"), en ook als echte navigatielink: elke tegel gaat naar
          de eigen categoriepagina (/product-category/…) i.p.v. hier ter
          plekke te filteren, zodat bezoekers de hero/subcategorieën/SEO-
          content van die categoriepagina te zien krijgen. */}
      <CategoryTileSlider
        eagerCount={2}
        tiles={SHOP_TILES.map((tile) => ({
          key: tile.slug,
          // MOW-FILTERPANEEL-NOV26: zolang alleen de eerste pagina er is, telt countFor ruim te laag (5 i.p.v. 136) en
          // liep het getal een paar seconden op. Liever even geen getal dan een verkeerd getal.
          label: volledig || laadMislukt ? `${tile.label} (${countFor(tile.slug)})` : tile.label,
          image: categoryBanners[tile.slug] || tile.image,
          imageKey: `shop.tile.${tile.slug}`,
          href: `/product-category/${tile.slug}`,
        }))}
      />

      {/* MOW-CATEGORIEFILTER-OKT26: de hoofdcategorieën als blokken, zonder iets te openen zichtbaar (filteren ter plekke) */}
      <CategoryBlocks blocks={HOOFD_CATEGORIEEN} filters={filtersMetSale} onChange={changeFilters} tree={categoryTree} />

      <div className="shop-layout">
        {/* SIDEBAR */}
        {/* MOW-FILTERS-OKT26 2: gescopeerde producten (merklijst); 3: resultCount (kaarten) voor de teller en de paneelknop; 1: changeFilters zet de pagina terug.
            MOW-CATEGORIEFILTER-OKT26: boom bovenaan het paneel (de hele boom uit het menu), Sale als filter, aantallen pas bij een complete lijst */}
        <ShopSidebar
          products={scopedProducts}
          filters={filtersMetSale}
          onChange={changeFilters}
          resultCount={aantalKaarten}
          defaultSort={paginaStandaardSort}
          fallbackCategories={HOOFD_CATEGORIEEN}
          listComplete={volledig}
          laadMislukt={laadMislukt} // MOW-FILTERPANEEL-NOV26
          saleCheck={isOnSale}
        />

        {/* PRODUCTENLIJST */}
        <div>
          <h1 style={{ fontSize: "1.75rem", marginBottom: "20px", color: "var(--color-secondary)" }}>
            {saleOnly
              ? (saleCategorie?.heroTitle || "Sale — alle aanbiedingen & acties")
              : nieuwOnly
              ? "Nieuwste producten"
              : <span data-t-key="shop.title">{t("shop.title", "Winkel")}</span>}
          </h1>
          {saleOnly && (
            <p style={{ marginTop: "-10px", marginBottom: "20px", color: "var(--color-gray-600)", fontSize: "0.9rem", maxWidth: "60ch", textAlign: "justify" }}>
              {saleCategorie?.description || "Alle afgeprijsde artikelen en producten die onder een lopende actie vallen, op één plek."}
            </p>
          )}
          {nieuwOnly && (
            <p style={{ marginTop: "-10px", marginBottom: "20px", color: "var(--color-gray-600)", fontSize: "0.9rem", maxWidth: "60ch" }} data-t-key="shop.nieuw.intro">
              {t("shop.nieuw.intro", "De nieuwste toevoegingen aan onze collectie.")}
            </p>
          )}

          {/* Actieve filter-badges — MOW-FILTERS-OKT26 4: nu voor alle filters (maat, materiaal en prijs erbij) */}
          <ActiveFilterChips filters={filtersMetSale} onChange={changeFilters} />

          <div ref={gridRef}>
          {/* MOW-FILTERS-OKT26 3: "N producten" (kaarten), niet meer "N resultaten" */}
          <p className="results-count" style={{ marginBottom: "20px" }}>
            {wachtOpLijst ? "Laden..." : formatProductCount(aantalKaarten)}
          </p>

          {wachtOpLijst ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-gray-400)" }}>Producten laden...</div>
          ) : visible.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-gray-400)" }}>
              <p data-t-key="shop.empty.title">{t("shop.empty.title", "Geen producten gevonden.")}</p>
              <button
                onClick={() => changeFilters({ ...DEFAULT_FILTERS, sort: paginaStandaardSort, sale: saleOnly })} // MOW-CATEGORIEFILTER-OKT26: de Sale-weergave blijft staan, zoals voorheen
                className="btn btn-outline"
                style={{ marginTop: "12px" }}
                data-t-key="shop.empty.clear-filters"
              >
                {t("shop.empty.clear-filters", "Filters wissen")}
              </button>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {pageCards.map((c) => (
                  <ProductCard key={c.key} product={c.product} colorOption={c.colorOption} headingLevel={2} />
                ))}
              </div>
              {/* MOW-PERF-OKT26: pas met de volledige lijst; de eerste pagina uit de server kent alleen de kleuren van die dertig producten, dus het aantal pagina's zou niet kloppen */}
              {(volledig || initialProducts.length === 0 || initialTotalCards === undefined) && <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />}
            </>
          )}
          </div>
        </div>
      </div>

      {/* SEO-blokken/FAQ/uitgelicht van de categorie "Sale" — alleen op de sale-weergave. */}
      {saleOnly && (
        <>
          <CategoryContentAccordion title="Sale" contentBlocks={saleCategorie?.contentBlocks} />
          {saleFeaturedProducts.length > 0 && <FeaturedProductsSlider products={saleFeaturedProducts} />}
          <CategoryFaqSection title="Sale" faqs={saleCategorie?.faqs} />
        </>
      )}
    </div>
  );
}

// useSearchParams vereist een Suspense-grens.
export default function ShopPageClient({ initialProducts, initialTotalCards, initialCategoryBanners, initialSaleCategorie }: { initialProducts: Product[]; initialTotalCards?: number; initialCategoryBanners?: Record<string, string | undefined>; initialSaleCategorie?: RichCategory }) {
  return (
    <Suspense fallback={null}>
      <ShopPageInner initialProducts={initialProducts} initialTotalCards={initialTotalCards} initialCategoryBanners={initialCategoryBanners} initialSaleCategorie={initialSaleCategorie} />
    </Suspense>
  );
}
